import * as React from 'react'
import { FC, useCallback, useMemo } from 'react'
import { Formik, FormikHelpers } from 'formik'
import { ILeadOppProduct } from '@user/store/leadOppProducts/typings'
import { IServiceProduct } from '@user/store/serviceProducts/typings'
import { IServiceFrequency } from '@user/store/serviceFrequency/typings'
import { AddServiceFormFields } from './AddServiceFormFields'
import {
  denormalize,
  ILeadOppProductFormModel,
  initialModel,
  normalize,
  validate,
} from './typings'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  createLeadOppProduct,
  updateLeadOppProduct,
} from '@user/store/leadOppProducts/api'
import {
  incrementLeadServicesCount,
  selectLeadDetail,
} from '@user/store/lead/slice'
import {
  incrementOppServicesCount,
  selectOpportunityDetail,
} from '@user/store/opportunity/slice'
import { ENTITY_TYPES } from '@sales-center/common'
import {
  addLeadOppProduct,
  editLeadOppProduct,
} from '@user/store/leadOppProducts/slice'
import { LocationDTO } from '@sales-center/common'
import { getLeadDetail, getLeadValue } from '@user/store/lead'
import {
  getOpportunityDetail,
  getOpportunityValue,
} from '@user/store/opportunity'
import { useAppSelector } from '@user/store/hooks'
import { toast } from '@sales-center/components'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
} from '@user/store/userPreferences/userPreferencesGeneral'

export interface Props {
  service?: ILeadOppProduct
  onClose: () => void
  serviceProducts: IServiceProduct[]
  serviceFrequencies: IServiceFrequency[]
  serviceLocations: LocationDTO[]
  entityId: number
  entityType: ENTITY_TYPES
  localMode: boolean
  nextSortOrder: Map<string, number>
  isMobile: boolean
}

export const AddServiceForm: FC<Props> = (props) => {
  const {
    entityId,
    entityType,
    service = { ...initialModel },
    onClose,
    localMode,
    serviceLocations,
    nextSortOrder,
  } = props

  const editMode = service.id !== -1
  const dispatch = useAppDispatch()

  const initialValues = useMemo(
    () => denormalize(service, serviceLocations, localMode),
    [localMode, service, serviceLocations],
  )
  const detail = useAppSelector((state) =>
    entityType === ENTITY_TYPES.Lead
      ? selectLeadDetail(state)
      : selectOpportunityDetail(state),
  )

  const onSubmitLocal = useCallback(
    (
      formModel: ILeadOppProductFormModel,
      helpers: FormikHelpers<ILeadOppProductFormModel>,
    ) => {
      if (editMode)
        dispatch(editLeadOppProduct(normalize(formModel, undefined)))
      else {
        formModel.sortOrder = nextSortOrder.has(formModel.serviceLocationId)
          ? nextSortOrder.get(formModel.serviceLocationId)!
          : 0
        dispatch(addLeadOppProduct(normalize(formModel, undefined)))
      }
      helpers.setSubmitting(false)
      onClose()
    },
    [dispatch, editMode, onClose],
  )

  const onSubmit = useCallback(
    async (formModel: ILeadOppProductFormModel) => {
      const model = normalize(formModel, entityId)

      // TEMP DEBUG - WWM-18150 - remove before merging
      // Capturing the exact entityId/serviceLocationId/serviceLocations in play at the moment
      // a service gets submitted, to catch the wrong-location bug live rather than by theory.

      console.log('[WWM-18150 DEBUG] AddServiceForm.onSubmit', {
        timestamp: new Date().toISOString(),
        entityId,
        entityType,
        modelId: model.id,
        modelServiceLocationId: model.serviceLocationId,
        formServiceLocationId: formModel.serviceLocationId,
        serviceLocationsCount: serviceLocations?.length,
        serviceLocationIds: serviceLocations?.map((l) => l.id),
      })

      if (model.id === -1) {
        dispatch(
          saveGeneralPreferences({
            type: IUserPreferenceGeneralListItemType.EntityAnnualServiceOccurrencesSubtractOne,
            value: formModel.subtractOne ? 'yes' : 'no',
          }),
        )
        model.sortOrder = nextSortOrder.has(model.serviceLocationId)
          ? nextSortOrder.get(model.serviceLocationId)!
          : 0
        const result = await dispatch(
          createLeadOppProduct({ product: model, entityType, entityId }),
        )

        if (createLeadOppProduct.fulfilled.match(result)) {
          if (entityType === ENTITY_TYPES.Lead) {
            dispatch(incrementLeadServicesCount({ leadId: entityId }))
            if (detail?.id == entityId) dispatch(getLeadDetail(entityId))
            dispatch(getLeadValue(entityId))
          } else {
            dispatch(incrementOppServicesCount({ oppId: entityId }))
            if (detail?.id == entityId) dispatch(getOpportunityDetail(entityId))
            dispatch(getOpportunityValue(entityId))
          }

          onClose()
        }
      } else {
        const result = await dispatch(
          updateLeadOppProduct({ product: model, entityType }),
        )

        if (updateLeadOppProduct.fulfilled.match(result)) {
          if (detail?.id == entityId) {
            if (entityType === ENTITY_TYPES.Lead) {
              dispatch(getLeadDetail(entityId))
            } else {
              dispatch(getOpportunityDetail(entityId))
            }
          }

          if (entityType === ENTITY_TYPES.Lead) {
            dispatch(getLeadValue(entityId))
          } else {
            dispatch(getOpportunityValue(entityId))
          }

          if (result.payload.draftAgreementsUpdated) {
            toast.success(
              'Service information has been updated on any draft agreements.',
            )
          }

          onClose()
        }
      }
    },
    [dispatch, entityId, entityType, onClose, detail],
  )
  return (
    // WWM-18150 - key forces Formik to fully remount (and re-capture initialValues) whenever
    // the entity being worked on, or which service is being edited, changes. Without this,
    // Formik only reads `initialValues` on its very first mount (it does not react to the prop
    // changing afterward), so if this form's underlying drawer stays mounted across switching to
    // a different opportunity/lead via in-app navigation (no full page reload), it silently kept
    // serving the *first* opportunity's serviceLocationId - including its auto-defaulted single
    // service location - for every later Add Service action in the same browser session,
    // corrupting the new product's ServiceLocationId to point at the wrong entity entirely.
    <Formik
      key={`${entityId}-${service.id}`}
      initialValues={initialValues}
      validate={(model) => validate(model)}
      onSubmit={localMode ? onSubmitLocal : onSubmit}
    >
      <AddServiceFormFields {...props} editMode={editMode} />
    </Formik>
  )
}
AddServiceForm.displayName = 'AddServiceForm'
